import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'

const DialogContext = createContext(null)

export const useDialog = () => {
  const ctx = useContext(DialogContext)
  if (!ctx) throw new Error('useDialog must be used within DialogProvider')
  return ctx
}

const BaseModal = ({ open, onClose, title, children, actions }) => {
  if (!open) return null
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose}></div>
      <div className="relative bg-white w-full max-w-md mx-4 rounded-xl shadow-xl p-6">
        {title ? <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3> : null}
        <div className="text-gray-700 mb-6">{children}</div>
        <div className="flex gap-3 justify-end">{actions}</div>
      </div>
    </div>
  )
}

export const DialogProvider = ({ children }) => {
  const [state, setState] = useState({ open: false })

  const close = useCallback(() => setState({ open: false }), [])

  const confirm = useCallback(({ title = 'Confirm', message, confirmText = 'Confirm', cancelText = 'Cancel', tone = 'primary' }) => {
    return new Promise((resolve) => {
      const onCancel = () => { close(); resolve(false) }
      const onConfirm = () => { close(); resolve(true) }
      setState({
        open: true,
        render: (
          <BaseModal open onClose={onCancel} title={title}
            actions={(
              <>
                <button type="button" onClick={onCancel} className="px-4 py-2 rounded-md bg-gray-200 text-gray-800 hover:bg-gray-300">{cancelText}</button>
                <button type="button" onClick={onConfirm} className={`px-4 py-2 rounded-md text-white ${tone === 'danger' ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}>{confirmText}</button>
              </>
            )}
          >
            {message}
          </BaseModal>
        )
      })
    })
  }, [close])

  const alert = useCallback(({ title = 'Notice', message, okText = 'OK' }) => {
    return new Promise((resolve) => {
      const onOk = () => { close(); resolve() }
      setState({
        open: true,
        render: (
          <BaseModal open onClose={onOk} title={title}
            actions={<button type="button" onClick={onOk} className="px-4 py-2 rounded-md bg-green-600 text-white hover:bg-green-700">{okText}</button>}
          >
            {message}
          </BaseModal>
        )
      })
    })
  }, [close])

  const value = useMemo(() => ({ confirm, alert }), [confirm, alert])

  return (
    <DialogContext.Provider value={value}>
      {children}
      {state.open ? state.render : null}
    </DialogContext.Provider>
  )
}
